/**
 * LiveStream — Live video streaming page (TikTok/Instagram-live style).
 *
 * Built on the same room infrastructure as audio rooms (room_type='video'):
 * the host streams camera+mic, viewers watch and can request to talk; the
 * host approves up to `max_speakers` on-stage participants (incl. host).
 * All room state and the LiveKit connection live in AudioRoomContext, so a
 * stream keeps playing while navigating (same mini-player behavior).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAudioRoom } from '../../Context/AudioRoomContext';
import API from '../../Network/API';
import './LiveStream.css';

// ─── Video tile: attaches a LiveKit video track, falls back to avatar ───
const VideoTile = ({ track, name, avatar, isSelf, isSpeaking, isMicOpen, roleLabel }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        const el = videoRef.current;
        if (track && el) {
            track.attach(el);
            return () => {
                try { track.detach(el); } catch (e) { /* detached already */ }
            };
        }
    }, [track]);

    return (
        <div className={`ls-tile ${isSpeaking ? 'ls-tile-speaking' : ''}`}>
            {track ? (
                <video ref={videoRef} autoPlay playsInline muted={isSelf} className={isSelf ? 'ls-mirror' : ''} />
            ) : (
                <div className="ls-tile-fallback">
                    {avatar
                        ? <img src={avatar} alt={name} />
                        : <div className="ls-tile-initial">{(name || 'U').charAt(0).toUpperCase()}</div>}
                </div>
            )}
            <div className="ls-tile-label">
                <span className="ls-tile-mic">{isMicOpen ? '🎤' : '🔇'}</span>
                <span className="ls-tile-name">{isSelf ? 'You' : (name || 'User')}</span>
                {roleLabel && <span className="ls-tile-role">{roleLabel}</span>}
            </div>
        </div>
    );
};

const LiveStream = () => {
    const {
        activeRoom, currentRole, isConnected, isMuted, currentUser,
        isSpeaker, canManage, reactions, chatMessages, activeSpeakers,
        unmutedParticipants, handRaised, speakerInvite, isRecording,
        remoteVideoTracks, isCameraOn, toggleCamera, switchCamera,
        localVideoTrack, toggleMute, requestToSpeak, lowerHand,
        handleCreateRoom, handleJoinRoom, handleLeaveRoom, handleEndRoom,
        refreshRoomDetails, sendReaction, acceptSpeakInvite, rejectSpeakInvite,
        setChatMessages, navigate,
    } = useAudioRoom();

    const { roomId } = useParams();
    const nav = useNavigate();

    // ─── List state ───
    const [liveStreams, setLiveStreams] = useState([]);
    const [loadingList, setLoadingList] = useState(true);

    // ─── Create modal state ───
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [title, setTitle] = useState('');
    const [maxSpeakers, setMaxSpeakers] = useState(4);
    const [recordEnabled, setRecordEnabled] = useState(false);
    const [notifyContacts, setNotifyContacts] = useState(true); // default ON
    const [creating, setCreating] = useState(false);

    // ─── In-room UI state ───
    const [showChat, setShowChat] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [showRequests, setShowRequests] = useState(false);
    const chatEndRef = useRef(null);

    const inVideoRoom = activeRoom && isConnected && activeRoom.room_type === 'video';

    // ─── Fetch live video streams (list view) ───
    const fetchStreams = useCallback(async () => {
        try {
            const res = await API.get('/v1/audio-rooms?status=live&type=video');
            // Client-side guard: an outdated backend ignores the ?type filter and
            // returns audio rooms here — never show a room the stream page can't enter.
            if (res.data?.success) {
                setLiveStreams((res.data.data || []).filter(r => r.room_type === 'video'));
            }
        } catch (e) {
            console.error('Fetch live streams failed:', e);
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        if (!inVideoRoom) {
            fetchStreams();
            const t = setInterval(fetchStreams, 30000);
            return () => clearInterval(t);
        }
    }, [inVideoRoom, fetchStreams]);

    // Deep link: /live-stream/join/:roomId
    useEffect(() => {
        if (roomId && !activeRoom) {
            (async () => {
                const ok = await handleJoinRoom(roomId);
                if (ok) nav('/live-stream', { replace: true });
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId]);

    // Deep link: /live-stream?action=create → open the Go Live modal directly
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('action') === 'create') {
            setShowCreateModal(true);
            window.history.replaceState(null, '', '/live-stream'); // clean the URL
        }
    }, []);

    // Auto-scroll chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages, showChat]);

    // ─── Actions ───
    const onGoLive = async () => {
        if (!title.trim()) { toast.error('Give your stream a title'); return; }
        setCreating(true);
        const ok = await handleCreateRoom(title, '', 'General', null, recordEnabled, {
            roomType: 'video',
            maxSpeakers,
            notifyContacts,
        });
        setCreating(false);
        if (ok === true) {
            setShowCreateModal(false);
            setTitle('');
        } else {
            toast.error('Could not start the stream — try again.');
        }
    };

    const onWatch = async (id) => {
        const ok = await handleJoinRoom(id);
        if (!ok) toast.error('Could not join this stream.');
    };

    const approveRequest = async (userId) => {
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/promote/${userId}`);
            refreshRoomDetails();
        } catch (e) {
            if (e.response?.status === 422) {
                toast.error(e.response?.data?.message || 'Stage is full', { autoClose: 2500 });
            } else {
                console.error('Promote failed:', e);
            }
        }
    };

    const declineRequest = async (userId) => {
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/reject-speak/${userId}`);
            refreshRoomDetails();
        } catch (e) { console.error('Reject failed:', e); }
    };

    const demoteFromStage = async (userId) => {
        try {
            await API.post(`/v1/audio-rooms/${activeRoom.id}/demote/${userId}`);
            refreshRoomDetails();
        } catch (e) { console.error('Demote failed:', e); }
    };

    const sendChat = async () => {
        const text = chatInput.trim();
        if (!text || !activeRoom) return;
        setChatInput('');
        try {
            // Server persists AND broadcasts chat_message over the data channel;
            // add optimistically so the sender sees it instantly (deduped by id).
            const res = await API.post(`/v1/audio-rooms/${activeRoom.id}/messages`, { message: text });
            const saved = res.data?.data;
            if (saved?.id) {
                setChatMessages(prev => prev.some(m => m.id === saved.id) ? prev : [...prev, saved]);
            }
        } catch (e) { console.error('Chat send failed:', e); }
    };

    const onLeave = async () => {
        if (currentRole === 'host') {
            if (window.confirm('End the live stream for everyone?')) {
                await handleEndRoom();
                nav('/live-stream');
            }
        } else {
            await handleLeaveRoom();
            nav('/live-stream');
        }
    };

    // ─── Derived stage/viewer data ───
    const participants = activeRoom?.participants || [];
    const stage = participants.filter(p => ['host', 'co_host', 'speaker'].includes(p.role));
    const viewers = participants.filter(p => p.role === 'listener');
    const pendingRequests = participants.filter(p => p.role === 'listener' && p.hand_raised_at);
    const maxStage = activeRoom?.max_speakers || 4;
    const stageFull = stage.length >= maxStage;

    const findVideoTrack = (userId) => {
        if (parseInt(userId) === currentUser?.id) {
            return isCameraOn ? localVideoTrack.current : null;
        }
        const key = Object.keys(remoteVideoTracks).find(k => k.startsWith(`${userId}_`));
        return key ? remoteVideoTracks[key] : null;
    };
    const idMatches = (set, userId) => [...set].some(k => k.startsWith(`${userId}_`));

    // ══════════════════════ ACTIVE STREAM VIEW ══════════════════════
    if (inVideoRoom) {
        const gridClass = stage.length <= 1 ? 'ls-grid-1' : stage.length === 2 ? 'ls-grid-2' : stage.length <= 4 ? 'ls-grid-4' : 'ls-grid-many';

        return (
            <div className="ls-page ls-live">
                {/* Header */}
                <div className="ls-header">
                    <div className="ls-header-info">
                        <span className="ls-live-badge">● LIVE</span>
                        {isRecording && <span className="ls-rec-badge">REC</span>}
                        <span className="ls-title">{activeRoom.title}</span>
                    </div>
                    <div className="ls-header-meta">
                        <span className="ls-viewers">👁 {viewers.length}</span>
                        <span className="ls-stage-count">🎤 {stage.length}/{maxStage}</span>
                    </div>
                </div>

                {/* Stage grid */}
                <div className={`ls-stage ${gridClass}`}>
                    {stage.map(p => (
                        <VideoTile
                            key={p.user_id}
                            track={findVideoTrack(p.user_id)}
                            name={p.user?.name}
                            avatar={p.user?.image}
                            isSelf={parseInt(p.user_id) === currentUser?.id}
                            isSpeaking={idMatches(activeSpeakers, p.user_id)}
                            isMicOpen={idMatches(unmutedParticipants, p.user_id)}
                            roleLabel={p.role === 'host' ? 'Host' : p.role === 'co_host' ? 'Co-host' : null}
                        />
                    ))}
                </div>

                {/* Floating reactions */}
                <div className="ls-reactions-overlay">
                    {reactions.map(r => (
                        <span key={r.id} className="ls-reaction-float">{r.emoji}</span>
                    ))}
                </div>

                {/* Speaker invite banner (viewer invited by host) */}
                {speakerInvite && (
                    <div className="ls-banner">
                        <span>🎤 {speakerInvite.fromName} invited you to talk</span>
                        <button className="ls-btn ls-btn-green" onClick={acceptSpeakInvite}>Accept</button>
                        <button className="ls-btn ls-btn-grey" onClick={rejectSpeakInvite}>Decline</button>
                    </div>
                )}

                {/* Host: pending talk requests */}
                {canManage && pendingRequests.length > 0 && (
                    <div className="ls-banner ls-banner-requests">
                        {pendingRequests.length === 1 ? (
                            <>
                                <span>✋ {pendingRequests[0].user?.name || 'Someone'} wants to talk</span>
                                <button
                                    className="ls-btn ls-btn-red"
                                    disabled={stageFull}
                                    title={stageFull ? 'Stage is full' : ''}
                                    onClick={() => approveRequest(pendingRequests[0].user_id)}
                                >{stageFull ? 'Stage full' : 'Approve'}</button>
                                <button className="ls-btn ls-btn-grey" onClick={() => declineRequest(pendingRequests[0].user_id)}>Decline</button>
                            </>
                        ) : (
                            <>
                                <span>✋ {pendingRequests.length} requests to talk</span>
                                <button className="ls-btn ls-btn-red" onClick={() => setShowRequests(true)}>Review</button>
                            </>
                        )}
                    </div>
                )}

                {/* Chat overlay (last messages, TikTok style) */}
                {!showChat && chatMessages.length > 0 && (
                    <div className="ls-chat-overlay">
                        {chatMessages.slice(-4).map(m => (
                            <div key={m.id} className="ls-chat-pill">
                                <b>{m.user?.name || 'User'}:</b> {m.message}
                            </div>
                        ))}
                    </div>
                )}

                {/* Bottom controls */}
                <div className="ls-controls">
                    <button className="ls-ctrl" onClick={() => setShowChat(v => !v)} title="Chat">💬</button>

                    {isSpeaker ? (
                        <>
                            <button className={`ls-ctrl ${isMuted ? 'ls-ctrl-off' : 'ls-ctrl-on'}`} onClick={toggleMute} title="Microphone">
                                {isMuted ? '🔇' : '🎤'}
                            </button>
                            <button className={`ls-ctrl ${isCameraOn ? 'ls-ctrl-on' : 'ls-ctrl-off'}`} onClick={toggleCamera} title="Camera">
                                {isCameraOn ? '📹' : '📷'}
                            </button>
                            {isCameraOn && (
                                <button className="ls-ctrl" onClick={switchCamera} title="Flip camera">🔄</button>
                            )}
                        </>
                    ) : (
                        <button
                            className={`ls-ctrl ${handRaised ? 'ls-ctrl-hand' : ''}`}
                            onClick={handRaised ? lowerHand : requestToSpeak}
                            title={handRaised ? 'Lower hand' : 'Request to talk'}
                        >✋</button>
                    )}

                    <button className="ls-ctrl" onClick={() => sendReaction('❤️')} title="React">❤️</button>

                    {canManage && (
                        <button className="ls-ctrl" onClick={() => setShowRequests(true)} title="Manage stage">
                            👥{pendingRequests.length > 0 && <span className="ls-dot" />}
                        </button>
                    )}

                    <button className="ls-ctrl ls-ctrl-leave" onClick={onLeave} title={currentRole === 'host' ? 'End stream' : 'Leave'}>
                        {currentRole === 'host' ? '⏹' : '✕'}
                    </button>
                </div>

                {/* Chat panel */}
                {showChat && (
                    <div className="ls-panel">
                        <div className="ls-panel-head">
                            <span>Chat</span>
                            <button onClick={() => setShowChat(false)}>✕</button>
                        </div>
                        <div className="ls-panel-body">
                            {chatMessages.map(m => (
                                <div key={m.id} className="ls-msg">
                                    <b>{m.user?.name || 'User'}</b>
                                    <span>{m.message}</span>
                                </div>
                            ))}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="ls-panel-input">
                            <input
                                value={chatInput}
                                onChange={e => setChatInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && sendChat()}
                                placeholder="Say something…"
                                maxLength={500}
                            />
                            <button onClick={sendChat}>Send</button>
                        </div>
                    </div>
                )}

                {/* Stage / requests manager (host) */}
                {showRequests && canManage && (
                    <div className="ls-panel">
                        <div className="ls-panel-head">
                            <span>Stage ({stage.length}/{maxStage})</span>
                            <button onClick={() => setShowRequests(false)}>✕</button>
                        </div>
                        <div className="ls-panel-body">
                            {pendingRequests.length > 0 && <div className="ls-panel-section">Requests</div>}
                            {pendingRequests.map(p => (
                                <div key={p.user_id} className="ls-row">
                                    <span>✋ {p.user?.name || 'User'}</span>
                                    <div>
                                        <button
                                            className="ls-btn ls-btn-red"
                                            disabled={stageFull}
                                            onClick={() => approveRequest(p.user_id)}
                                        >{stageFull ? 'Full' : 'Approve'}</button>
                                        <button className="ls-btn ls-btn-grey" onClick={() => declineRequest(p.user_id)}>✕</button>
                                    </div>
                                </div>
                            ))}
                            <div className="ls-panel-section">On stage</div>
                            {stage.map(p => (
                                <div key={p.user_id} className="ls-row">
                                    <span>{p.user?.name || 'User'} {p.role === 'host' ? '👑' : ''}</span>
                                    {p.role !== 'host' && parseInt(p.user_id) !== currentUser?.id && (
                                        <button className="ls-btn ls-btn-grey" onClick={() => demoteFromStage(p.user_id)}>Remove</button>
                                    )}
                                </div>
                            ))}
                            <div className="ls-panel-section">Viewers ({viewers.length})</div>
                            {viewers.map(p => (
                                <div key={p.user_id} className="ls-row">
                                    <span>{p.user?.name || 'User'}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ══════════════════════ LIST / LANDING VIEW ══════════════════════
    return (
        <div className="ls-page">
            <div className="ls-list-header">
                <h2>🔴 Live Streams</h2>
                <button className="ls-btn ls-btn-red ls-golive" onClick={() => setShowCreateModal(true)}>
                    Go Live
                </button>
            </div>

            {loadingList ? (
                <div className="ls-empty">Loading…</div>
            ) : liveStreams.length === 0 ? (
                <div className="ls-empty">
                    <p>No one is live right now.</p>
                    <p>Start your own stream and your post appears in the timeline.</p>
                </div>
            ) : (
                <div className="ls-list">
                    {liveStreams.map(room => (
                        <div key={room.id} className="ls-card" onClick={() => onWatch(room.id)}>
                            <div className="ls-card-top">
                                <span className="ls-live-badge">● LIVE</span>
                                <span className="ls-viewers">👁 {room.listener_count || 0}</span>
                            </div>
                            <div className="ls-card-title">{room.title}</div>
                            <div className="ls-card-host">
                                {room.host?.image && <img src={room.host.image} alt="" />}
                                <span>{room.host?.name || 'Host'}</span>
                            </div>
                            <button className="ls-btn ls-btn-red">Watch</button>
                        </div>
                    ))}
                </div>
            )}

            {/* Create modal */}
            {showCreateModal && (
                <div className="ls-modal-backdrop" onClick={() => !creating && setShowCreateModal(false)}>
                    <div className="ls-modal" onClick={e => e.stopPropagation()}>
                        <h3>Go Live</h3>
                        <textarea
                            value={title}
                            onChange={e => setTitle(e.target.value.slice(0, 60))}
                            placeholder="What is your stream about?"
                            rows={2}
                            maxLength={60}
                        />
                        <label className="ls-field">
                            <span>People talking at once (including you)</span>
                            <select value={maxSpeakers} onChange={e => setMaxSpeakers(parseInt(e.target.value))}>
                                {[2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                        <label className="ls-toggle">
                            <input type="checkbox" checked={notifyContacts} onChange={e => setNotifyContacts(e.target.checked)} />
                            <span>Notify my contacts</span>
                        </label>
                        <label className="ls-toggle">
                            <input type="checkbox" checked={recordEnabled} onChange={e => setRecordEnabled(e.target.checked)} />
                            <span>Record stream</span>
                        </label>
                        <div className="ls-modal-actions">
                            <button className="ls-btn ls-btn-grey" disabled={creating} onClick={() => setShowCreateModal(false)}>Cancel</button>
                            <button className="ls-btn ls-btn-red" disabled={creating} onClick={onGoLive}>
                                {creating ? 'Starting…' : '🔴 Go Live'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveStream;
